import { useEffect, useState } from 'react';
import { Icon } from './icons/Icon';
import { Modal, Button, useToast } from './primitives';
import { api } from '../lib/api';
import { MojeUdajeButton } from '../features/ucet/MojeUdajeButton';
import styles from './HeaderInfoButtons.module.css';

/**
 * Tři informační záložky z hlavičky staré verze (frontend/index.html):
 *  - 📰 Novinky  → GET /api/news/ (RSS ze stavebnictví, 24h cache)
 *  - 🔗 Odkazy   → links.json (statický seznam užitečných odkazů)
 *  - 💬 Podněty  → POST /api/email/feedback (zpráva vývojovému týmu)
 *
 * Chování odpovídá původnímu designu, vizuál je portovaný do v2 (Modal + Button).
 */

type NewsArticle = {
  title: string;
  url: string;
  summary?: string;
  image?: string | null;
  source: string;
  lang?: string;
};

type QuickLink = { name: string; url: string };

// ── Novinky ───────────────────────────────────────────────────────────────────
function NovinkyModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [articles, setArticles] = useState<NewsArticle[] | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    setError(false);
    try {
      const data = await api.get<{ articles?: NewsArticle[] }>('/news/');
      setArticles(data.articles ?? []);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open && articles === null && !loading) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={
        <div className={styles.titleRow}>
          <span>📰 Novinky ze stavebnictví</span>
          <button
            type="button"
            className={styles.refreshBtn}
            onClick={() => void load()}
            disabled={loading}
            title="Obnovit"
          >
            ↻ Obnovit
          </button>
        </div>
      }
    >
      <p className={styles.subtle}>
        Aktualizuje se 1× denně • Novinky.cz, e15, iROZHLAS + Construction Dive 🇬🇧
      </p>
      {loading ? (
        <div className={styles.empty}>⏳ Načítám novinky…</div>
      ) : error ? (
        <div className={styles.errorBox}>✗ Nepodařilo se načíst novinky</div>
      ) : !articles || articles.length === 0 ? (
        <div className={styles.empty}>Žádné články k dispozici.</div>
      ) : (
        <ul className={styles.newsList}>
          {articles.map((a, i) => (
            <li key={a.url || i}>
              <a className={styles.newsItem} href={a.url} target="_blank" rel="noopener noreferrer">
                {a.image ? (
                  <img
                    className={styles.newsThumb}
                    src={a.image}
                    alt=""
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <span className={styles.newsThumbFallback} aria-hidden>
                    🏗️
                  </span>
                )}
                <span className={styles.newsBody}>
                  <span className={styles.newsTitle}>{a.title}</span>
                  {a.summary && <span className={styles.newsSummary}>{a.summary}</span>}
                  <span className={styles.newsSource}>
                    📌 {a.source}
                    {a.lang === 'en' ? ' 🇬🇧' : ''}
                  </span>
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

// ── Odkazy ────────────────────────────────────────────────────────────────────
function OdkazyModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [links, setLinks] = useState<QuickLink[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open || links !== null) return;
    setError(false);
    // Kořenová cesta — links.json servíruje v1 frontend na rootu domény
    // (funguje v produkci i v dev, kde leží v public/). Nepoužívat BASE_URL,
    // ten by ve v2 buildu mířil na /v2/links.json, který na serveru není.
    fetch('/links.json')
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data: QuickLink[]) => setLinks(data))
      .catch(() => setError(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Modal open={open} onClose={onClose} size="md" title="🔗 Rychlé odkazy">
      {error ? (
        <div className={styles.errorBox}>✗ Nelze načíst seznam odkazů</div>
      ) : links === null ? (
        <div className={styles.empty}>⏳ Načítám…</div>
      ) : (
        <ul className={styles.linkList}>
          {links.map((lnk, i) => (
            <li key={lnk.url || i}>
              <a className={styles.linkItem} href={lnk.url} target="_blank" rel="noopener noreferrer">
                🔗 {lnk.name}
              </a>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

// ── Podněty ───────────────────────────────────────────────────────────────────
function PodnetyModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  // Reset formuláře při každém otevření
  useEffect(() => {
    if (open) {
      setName('');
      setMessage('');
    }
  }, [open]);

  async function submit() {
    if (!name.trim() || !message.trim()) {
      toast.error('Vyplňte jméno i zprávu.');
      return;
    }
    setSending(true);
    try {
      await api.post('/email/feedback', { name: name.trim(), message: message.trim() });
      toast.success('Podnět odeslán, díky!');
      onClose();
    } catch {
      toast.error('Podnět se nepodařilo odeslat.');
    } finally {
      setSending(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title="💬 Podněty a připomínky"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={sending}>
            Zrušit
          </Button>
          <Button variant="primary" onClick={() => void submit()} disabled={sending}>
            {sending ? 'Odesílám…' : 'Odeslat podnět'}
          </Button>
        </>
      }
    >
      <p className={styles.subtle}>
        Napište nám váš podnět nebo připomínku. Odešle se přímo vývojovému týmu.
      </p>
      <label className={styles.formLabel}>
        Vaše jméno *
        <input
          className={styles.formInput}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Jan Novák"
        />
      </label>
      <label className={styles.formLabel}>
        Připomínka / podnět *
        <textarea
          className={styles.formTextarea}
          rows={5}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Napište svůj podnět, nápad nebo připomínku…"
        />
      </label>
    </Modal>
  );
}

// ── Tlačítka v hlavičce ───────────────────────────────────────────────────────
type ActiveModal = null | 'novinky' | 'odkazy' | 'podnety';

export function HeaderInfoButtons() {
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const close = () => setActiveModal(null);

  return (
    <>
      <MojeUdajeButton />
      <Button
        variant="ghost"
        size="sm"
        leading={<Icon name="newspaper" />}
        onClick={() => setActiveModal('novinky')}
        title="Novinky ze stavebnictví"
      >
        <span className="sd-phone-hide">Novinky</span>
      </Button>
      <Button
        variant="ghost"
        size="sm"
        leading={<Icon name="link" />}
        onClick={() => setActiveModal('odkazy')}
        title="Rychlé odkazy"
      >
        <span className="sd-phone-hide">Odkazy</span>
      </Button>
      <Button
        variant="secondary"
        size="sm"
        leading={<Icon name="message" />}
        onClick={() => setActiveModal('podnety')}
        title="Podněty a připomínky"
      >
        <span className="sd-phone-hide">Podněty</span>
      </Button>

      <NovinkyModal open={activeModal === 'novinky'} onClose={close} />
      <OdkazyModal open={activeModal === 'odkazy'} onClose={close} />
      <PodnetyModal open={activeModal === 'podnety'} onClose={close} />
    </>
  );
}
